import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import * as THREE from "three";
import { supabase } from "@/lib/supabaseClient";
import { generateTexture, p5Width, p5Height } from "./P5Sketch_Hope.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import EventEmitter from "./EventEmitter"; // or "../../EventEmitter" depending on location

async function canvasToPngBlob(canvas) {
  return await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
  });
}

async function uploadAndUpsert({ creationId, type, engine, canvas }) {
  const bucket = "creations-public";
  const path = `creations/${creationId}/${type}_${engine}.png`;

  const blob = await canvasToPngBlob(canvas);

  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(path, blob, { contentType: "image/png", upsert: true });
  if (uploadError) throw uploadError;

  const { error: dbError } = await supabase.from("creation_assets").upsert(
    {
      creation_id: creationId,
      type,
      engine,
      bucket,
      path,
      content_type: "image/png",
      width: canvas.width,
      height: canvas.height,
      bytes: blob.size,
    },
    { onConflict: "creation_id,type,engine" }
  );
  if (dbError) throw dbError;

  return { bucket, path };
}

const ThreeScene = forwardRef(function ThreeScene(_props, ref) {
  const controlsRef = useRef(null);
  const initialCameraRef = useRef(null);
  const initialTargetRef = useRef(null);

  const mountRef = useRef(null);
  const p5ContainerRef1 = useRef(null);
  const p5ContainerRef2 = useRef(null);

  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const p5InstanceRef = useRef(null);
  const p5Instance2Ref = useRef(null);

  const autoRotateRef = useRef(false);

  useImperativeHandle(ref, () => ({
    async saveAssets(creationId) {
      if (!creationId) throw new Error("Missing creationId");

      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      const controls = controlsRef.current;

      const p5Canvas = p5InstanceRef.current?.canvas;
      const webglCanvas = renderer?.domElement;

      if (!p5Canvas) throw new Error("p5 canvas not ready");
      if (!webglCanvas) throw new Error("three renderer not ready");

      // ---- Freeze p5 updates to avoid random redraw differences
      // (requires the small EventEmitter additions in P5Sketch_Hope.js)
      try { EventEmitter?.dispatch?.("freezeP5", true); } catch { }

      // 1) Save p5 as-is (no redraw)
      await uploadAndUpsert({ creationId, type: "final", engine: "p5", canvas: p5Canvas });

      // ---- Three.js: reset view + render to fixed square ----
      const EXPORT_SIZE = 1024; // change to 2048 later if you want

      // store current state to restore after capture
      const oldSize = new THREE.Vector2();
      renderer.getSize(oldSize);
      const oldPixelRatio = renderer.getPixelRatio();
      const oldAspect = camera.aspect;

      const oldCamPos = camera.position.clone();
      const oldCamQuat = camera.quaternion.clone();
      const oldTarget = controls?.target?.clone();

      try {
        // reset camera + controls to defaults (centered view)
        const initCam = initialCameraRef.current;
        const initTarget = initialTargetRef.current;
        if (initCam) {
          camera.position.copy(initCam.position);
          camera.quaternion.copy(initCam.quaternion);
          camera.fov = initCam.fov;
        }
        if (controls && initTarget) {
          controls.target.copy(initTarget);
          controls.update();
        }

        // set square output size
        renderer.setPixelRatio(1); // stable output size
        renderer.setSize(EXPORT_SIZE, EXPORT_SIZE, false);

        camera.aspect = 1;
        camera.updateProjectionMatrix();

        // render one crisp frame
        renderer.render(scene, camera);

        // upload square render
        await uploadAndUpsert({ creationId, type: "final", engine: "threejs", canvas: renderer.domElement });
      } finally {
        // restore interactive state
        renderer.setPixelRatio(oldPixelRatio);
        renderer.setSize(oldSize.x, oldSize.y, false);

        camera.aspect = oldAspect;
        camera.updateProjectionMatrix();

        camera.position.copy(oldCamPos);
        camera.quaternion.copy(oldCamQuat);

        if (controls && oldTarget) {
          controls.target.copy(oldTarget);
          controls.update();
        }

        try { EventEmitter?.dispatch?.("freezeP5", false); } catch { }
      }
    },

    // For control buttons
    zoomIn() {
      const camera = cameraRef.current;
      if (!camera) return;

      // simple + safe: move camera closer
      camera.position.z = Math.max(2, camera.position.z - 0.6);
      camera.updateProjectionMatrix();
    },

    zoomOut() {
      const camera = cameraRef.current;
      if (!camera) return;

      camera.position.z = Math.min(25, camera.position.z + 0.6);
      camera.updateProjectionMatrix();
    },

    resetView() {
      const camera = cameraRef.current;
      const controls = controlsRef.current;
      const initCam = initialCameraRef.current;
      const initTarget = initialTargetRef.current;

      if (camera && initCam) {
        camera.position.copy(initCam.position);
        camera.quaternion.copy(initCam.quaternion);
        camera.fov = initCam.fov;
        camera.updateProjectionMatrix();
      }

      if (controls && initTarget) {
        controls.target.copy(initTarget);
        controls.update();
      }
    },

    toggleAutoRotate() {
      const controls = controlsRef.current;
      if (!controls) return false;

      autoRotateRef.current = !autoRotateRef.current;
      controls.autoRotate = autoRotateRef.current;
      controls.autoRotateSpeed = 1.0; // tweak
      return autoRotateRef.current;
    },

    getAutoRotate() {
      return !!autoRotateRef.current;
    }

  }));


  useEffect(() => {
    if (!mountRef.current || !p5ContainerRef1.current || !p5ContainerRef2.current) {
      return;
    }

    let renderer, scene, camera, controls, animationId;
    let sizes = { width: 0, height: 0 };

    // Handle window resize
    const handleResize = () => {
      if (!mountRef.current || !camera || !renderer) return;
      sizes.width = mountRef.current.clientWidth;
      sizes.height = mountRef.current.clientHeight;

      camera.aspect = sizes.width / sizes.height;
      camera.updateProjectionMatrix();

      renderer.setSize(sizes.width, sizes.height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    };

    window.addEventListener("resize", handleResize);

    let cancelled = false;

    // Initialize the scene
    const init = async () => {
      const mountEl = mountRef.current; // capture stable ref
      const { p5Instance, p5Instance2 } = await generateTexture(
        p5ContainerRef1.current,
        p5ContainerRef2.current
      );

      p5InstanceRef.current = p5Instance;
      p5Instance2Ref.current = p5Instance2;

      // if user switched experiences, stop here
      if (cancelled) return;

      //  mount no longer exists / removed from DOM
      if (!mountEl || !mountEl.isConnected) return;

      // Scene
      scene = new THREE.Scene();

      // Background img
const bgLoader = new THREE.TextureLoader();
bgLoader.load("/images/blanket_bg_002.png", (bgTexture) => {
  bgTexture.colorSpace = THREE.SRGBColorSpace;
  scene.background = bgTexture;
});
      sceneRef.current = scene;

      // Textures (same settings as your original)
      const texture = new THREE.CanvasTexture(p5Instance.canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;

      const texture2 = new THREE.CanvasTexture(p5Instance2.canvas);
      texture2.colorSpace = THREE.SRGBColorSpace;
      texture2.generateMipmaps = false;
      texture2.minFilter = THREE.NearestFilter;
      texture2.magFilter = THREE.NearestFilter;
      texture2.center = new THREE.Vector2(0.5, 0.5);
      texture2.flipY = false;
      texture2.rotation = Math.PI;

      // Geometry with correct proportions (same as your original)
      const segments = 50;
      const widthOfCloth = 5;
      const geometry = new THREE.PlaneGeometry(
        widthOfCloth,
        p5Height / (p5Width / widthOfCloth),
        segments,
        segments
      );

      // Materials (front + back, same as original)
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        wireframe: false,
        transparent: false,
        opacity: 1,
      });

      const material2 = new THREE.MeshBasicMaterial({
        map: texture2,
        side: THREE.BackSide,
        wireframe: false,
        transparent: false,
        opacity: 0.6,
      });

      const plane = new THREE.Mesh(geometry, material);
      const plane2 = new THREE.Mesh(geometry, material2);
      scene.add(plane);
      scene.add(plane2);

      // Sizes
      sizes.width = mountEl.clientWidth || 1;
      sizes.height = mountEl.clientHeight || 1;

      // safe sizes
      const w = mountEl.clientWidth || 1;
      const h = mountEl.clientHeight || 1;

      // Camera
      camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000);
      camera.position.z = 8;
      camera.lookAt(scene.position);
      scene.add(camera);
      cameraRef.current = camera;

      // after camera is positioned
      initialCameraRef.current = {
        position: camera.position.clone(),
        quaternion: camera.quaternion.clone(),
        fov: camera.fov,
      };

      // renderer
      renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      rendererRef.current = renderer;

      mountEl.innerHTML = "";
      mountEl.appendChild(renderer.domElement);

      // Controls
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;

      // after controls created
      controlsRef.current = controls;
      initialTargetRef.current = controls.target.clone();


      // Animations (wave + texture updates, same as original)
      const waveFrequency = 2;
      const waveAmplitude = 0.1;
      const clock = new THREE.Clock();

      const animate = () => {
        animationId = requestAnimationFrame(animate);

        const elapsedTime = clock.getElapsedTime() * 2;

        const vertices = geometry.attributes.position.array;
        for (let i = 0; i < vertices.length; i += 3) {
          const x = vertices[i];
          const y = vertices[i + 1];
          const z = Math.sin((x + y) * waveFrequency + elapsedTime) * waveAmplitude;
          vertices[i + 2] = z;
        }
        geometry.attributes.position.needsUpdate = true;

        // crucial for live p5 updates
        if (texture.image && texture2.image) {
          texture.needsUpdate = true;
          texture2.needsUpdate = true;
        }

        controls.update();
        renderer.render(scene, camera);
      };

      animate();
    };

    init();

    return () => {
      cancelled = true;

      if (animationId) cancelAnimationFrame(animationId);
      if (renderer) renderer.dispose();
      if (controls) controls.dispose();
      window.removeEventListener("resize", handleResize);
      if (mountRef.current && renderer) mountRef.current.removeChild(renderer.domElement);

      if (p5InstanceRef.current) {
        p5InstanceRef.current.remove();
        p5InstanceRef.current = null;
      }
      if (p5Instance2Ref.current) {
        p5Instance2Ref.current.remove();
        p5Instance2Ref.current = null;
      }

    };


  }, []);

  return (
    <>
      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />
      <div ref={p5ContainerRef1} style={{ display: "none" }} />
      <div ref={p5ContainerRef2} style={{ display: "none" }} />
    </>
  );
});

export default ThreeScene;
